<template>
	<section>
		<div class="section-table">
			<pagination
				class="pagination-top"
				:total="insuranceDocuments.total"
				:page.sync="insuranceDocuments.current_page"
				:limit.sync="insuranceDocuments.per_page"
				@pagination="setInsuranceDocumentsData(false)"
				@sizeChange="setInsuranceDocumentsData(true)"
				:separate-change="true"
				:page-sizes="[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100, 200, 500]"
			>
				<el-input style="margin-left: 10px" size="small" v-model="search" placeholder="Search Insurance Documents"></el-input>
			</pagination>

			<el-table
				ref="insuranceDocuments"
				v-loading="tableLoading"
				:data="tableData"
				stripe
				border
				style="width: 100%"
				:row-key="setRowKeys"
				:default-sort="{ prop: 'weight', order: 'ascending' }"
			>
				<el-table-column prop="name" sortable label="Name">
					<template slot-scope="scope">
						<span>{{ scope.row.name }}</span>
					</template>
				</el-table-column>

				<el-table-column prop="insurer" sortable label="Insurer">
					<template slot-scope="scope">
						<span>{{ scope.row.insurance.name }}</span>
					</template>
				</el-table-column>

				<el-table-column prop="cover_types" sortable label="Cover Type/s">
					<template slot-scope="scope">
						<span>{{ commaSeparateArray(scope.row, "cover_types", "name") }}</span>
					</template>
				</el-table-column>

				<el-table-column prop="assets" sortable label="Asset/s">
					<template slot-scope="scope">
						<span>{{ commaSeparateArray(scope.row, "assets", "name") }}</span>
					</template>
				</el-table-column>

				<el-table-column prop="application_types" sortable label="App Type/s">
					<template slot-scope="scope">
						<span>{{ commaSeparateArray(scope.row, "application_types", "name") }}</span>
					</template>
				</el-table-column>

				<el-table-column prop="document_types" sortable label="Document Type/s">
					<template slot-scope="scope">
						<span>{{ commaSeparateArray(scope.row, "document_types", "name") }}</span>
					</template>
				</el-table-column>

				<el-table-column fixed="right" label="Action" width="180">
					<template slot-scope="scope">
						<el-dropdown class="w-100">
							<el-button :style="hasOverride(scope.row) ? 'color: green' : 'color: blue'" size="small">
								{{ hasOverride(scope.row) ? 'Tenant Document' : 'Global Document'}}
								<i class="el-icon-arrow-down el-icon--right"></i>
							</el-button>
							<el-dropdown-menu slot="dropdown">
								<el-dropdown-item @click.native="openDocumentsModal(scope.row)">
									<span style="color: blue">Change Document</span>
								</el-dropdown-item>
								<el-dropdown-item @click.native="viewDocument(scope.row)">
									<span style="color: green">View Document</span>
								</el-dropdown-item>
								<el-dropdown-item v-if="hasOverride(scope.row)" @click.native="deleteInsuranceDocument(scope.row)">
									<span style="color: red">Revert Document</span>
								</el-dropdown-item>
							</el-dropdown-menu>
						</el-dropdown>
					</template>
				</el-table-column>
			</el-table>

			<pagination
				class="pagination-bottom"
				:total="insuranceDocuments.total"
				:page.sync="insuranceDocuments.current_page"
				:limit.sync="insuranceDocuments.per_page"
				@pagination="setInsuranceDocumentsData(false)"
				@sizeChange="setInsuranceDocumentsData(true)"
				:page-sizes="[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100, 200, 500]"
			>
				<el-input style="margin-left: 10px" size="small" v-model="search" placeholder="Search Insurance Documents"></el-input>
			</pagination>

			<x-modal
				:wWidth="1080"
				v-if="visible"
				@cancel="closeDocumentsModal"
				@close="closeDocumentsModal"
				@ok="saveInsuranceDocument(true)"
				:draggable="true"
				:resizable="true"
				:loading="loading"
				:okDisabled="loading"
				okText="Save"
			>
				<div slot="title" :class="`d-flex justify-content-between align-items-center`">
					Update Insurance Document
				</div>

				<el-form ref="insuranceDocumentForm" :model="insuranceDocumentForm" :rules="insuranceDocumentFormRules">
                    <el-form-item label="Document Name">
                        <el-input :disabled="true" v-model="insuranceDocumentForm.name"></el-input>
                    </el-form-item>
					<el-form-item label="Document" prop="document">
                        <file-selector :current-file="insuranceDocumentForm.url" v-model="insuranceDocumentForm.document"></file-selector>
                    </el-form-item>
				</el-form>
			</x-modal>
		</div>
	</section>
</template>

<script>
	import { mapGetters } from "vuex"
	import Pagination from "@shared/components/pagination"
	import FileSelector from "@shared/components/upload/FileSelector"

	export default {
		name: "InsuranceDocuments",
		components: {
			Pagination,
			FileSelector,
		},
		data() {
			return {
				options: [],
				optionsLoading: true,
				tiersLoading: true,
				visible: false,
				loading: false,
				tableLoading: true,
				search: "",
				searchTimeout: null,
				searchTimeoutWait: 1000,
                tiersTimeout: null,
				tiersTimeoutWait: 2000,
				activeClicked: false,
				deleteClicked: false,
				insuranceDocumentForm: {},
				insuranceDocumentFormRules: {
					document: { required: true, message: "Document is Required", trigger: "change" }
				},
                cancelSource: null,
			}
		},
		mounted() {
			this.setInsuranceDocumentsData(true)
		},
		watch: {
			search: {
				handler() {
					if (this.searchTimeout) clearTimeout(this.searchTimeout)
					this.searchTimeout = setTimeout(() => {
						this.setInsuranceDocumentsData(true)
					}, this.searchTimeoutWait)
				},
			},
		},
		computed: {
			...mapGetters({
				insuranceDocuments: "insuranceDocuments/getPaginatedInsuranceDocuments",
			}),
			tableData() {
				if (_.isEmpty(this.insuranceDocuments.data)) return []
				return this.insuranceDocuments.data
			},
            currentTeamId() {
                return Ambition.currentTeamId()
            }
		},
		methods: {
            hasOverride(row) {
                if (!_.isEmpty(row.insurance_document_tenant)) {
                    if (this.currentTeamId && !_.isEmpty(row.insurance_document_tenant.team_overrides)) {
                        return row.insurance_document_tenant.team_overrides.some(override => override.overridable.team_id === this.currentTeamId)
                    } else if (!this.currentTeamId) {
                        return _.get(row, 'insurance_document_tenant.path') && _.get(row, 'insurance_document_tenant.url')
                    }
                }
                return false
            },
            cancelSearch() {
				if (this.cancelSource) {
					this.cancelSource.cancel("Filter Changed")
				}
			},
			viewDocument(row) {
				window.open(row.url, "_blank").focus()
			},
			commaSeparateArray(row, type, column) {
				if (_.isEmpty(row[type])) return "Not Set"
				return row[type].map((type) => type[column]).join(", ")
			},
			setRowKeys(row) {
				return row.id
			},
			openDocumentsModal(row) {

                let form = Object.assign({}, row)
                Vue.set(this, 'insuranceDocumentForm', form)

				// bug fix
				setTimeout(() => {
					this.$refs["insuranceDocumentForm"].clearValidate()
				}, 1)

				this.visible = true
				this.loading = false
			},
			closeDocumentsModal() {
				this.insuranceDocumentForm = {}
				this.loading = false
				this.visible = false
			},
			setInsuranceDocumentsData(resetPage) {
				if (resetPage) {
					this.insuranceDocuments.current_page = 1
				}
				let data = {
					page: this.insuranceDocuments.current_page,
					insuranceDocuments: this.insuranceDocuments,
					search: this.search,
				}
				this.tableLoading = true
				this.$store.dispatch("insuranceDocuments/setPaginatedInsuranceDocuments", data).then((res) => {
					this.tableLoading = false
				})
			},
			saveInsuranceDocument(redirect) {
				this.$refs["insuranceDocumentForm"].validate((valid) => {
					if (valid) {
						this.loading = true
						let headers = {
							headers: {
								"Content-Type": "multipart/form-data",
							},
						}
						let formData = new FormData()
						formData.append("insuranceDocumentForm", JSON.stringify(this.insuranceDocumentForm))
						formData.append("insuranceDocument", this.insuranceDocumentForm.document)
						this.$store.dispatch("insuranceDocuments/saveInsuranceDocument", formData, headers).then((res) => {
							this.closeDocumentsModal()
							swal({
								title: "Success!",
								type: "success",
								text: "The Insurance Document was successfully updated",
							})
						})
					}
				})
			},
			deleteInsuranceDocument(row) {

				this.deleteClicked = true
				let data = {
					id: row.id,
				}

				swal({
					title: "Revert Document",
					text: `You are about to revert this Insurance Document to its original.`,
					type: "warning",
					showCancelButton: true,
					cancelButtonText: "Cancel",
					cancelButtonColor: "#d33",
					confirmButtonText: "Revert",
					confirmButtonColor: "#284da5",
					reverseButtons: false,
				}).then((actioned) => {
					if (actioned.value) {
						this.$store.dispatch("insuranceDocuments/deleteInsuranceDocument", data).then((res) => {
							this.deleteClicked = false
						})
					} else {
						this.deleteClicked = false
					}
				})
			},
			// format date
			dateFormatter(row) {
				let updated = this.$moment.utc(row.updated_at).tz(this.user.timezone)
				if (updated) {
					return this.$moment.utc(row.updated_at).tz(this.user.timezone).format("ddd DD/MM/YY LT")
				}
			},
		},
	}
</script>

<style lang="scss">
	.tox-tinymce {
		.tox-statusbar__branding {
			display: none !important;
		}
	}

	.tox-tinymce-aux {
		z-index: 9999999999 !important;
		.tox-notifications-container {
			display: none !important;
		}
	}
</style>

<style lang="scss" scoped>
	.section-table {
		background-color: #f0f3f5;
		border: 1px solid #c2cfd6;
		padding: 10px;

		.pagination-container {
			border: 1px solid #dcdfe6;
			border-radius: 5px;
			padding: 10px !important;

			&.pagination-top {
				margin-bottom: 10px !important;
			}

			&.pagination-bottom {
				margin-top: 10px !important;
				margin-bottom: 0 !important;
			}

			.el-pager {
				li {
					background-color: transparent;
					border: 1px solid #dcdfe6;
					font-weight: 400;

					&.active {
						background-color: #69c23a;
						border: none;
					}
				}
			}
		}

		.el-table {
			border: 1px solid #dcdfe6;
			border-radius: 5px;

			.cell {
				word-break: break-word;
			}
		}
	}

	.el-form-item__label {
		width: 100%;
		text-align: left;
	}
</style>
